import React, { useState, useEffect, useMemo } from "react";
import {
    useGetPaymentLogsQuery,
    useGetPaymentStatisticsQuery,
    useExportPaymentLogsQuery,
} from "../api";
import { PaymentLogFilters } from "../types/paymentStats";
import { UserAutocomplete, User } from "../components/UserAutocomplete";

// Интерфейсы теперь импортируются из types/paymentStats.ts

const PaymentStatsPage: React.FC = () => {
    // Создаем даты более надежным способом
    const firstDayOfMonth = useMemo(() => {
        const now = new Date();
        const currentYear = now.getFullYear();
        const currentMonth = now.getMonth(); // 0-based (0 = январь, 9 = октябрь)
        return new Date(currentYear, currentMonth, 1);
    }, []);

    const today = useMemo(() => new Date(), []);

    // Функция для получения даты в формате YYYY-MM-DD в локальном времени
    const formatDateLocal = (date: Date): string => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
    };

    const [filters, setFilters] = useState<PaymentLogFilters>(() => {
        const dateFrom = formatDateLocal(firstDayOfMonth);
        const dateTo = formatDateLocal(today);

        return {
            user_id: "",
            result: "",
            date_from: dateFrom, // 1 число текущего месяца
            date_to: dateTo, // Сегодня (включая весь день)
            page: 1,
            per_page: 20,
        };
    });

    // Состояние для модалки с деталями
    const [selectedLog, setSelectedLog] = useState<any>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    // Состояние для выбранного пользователя
    const [selectedUser, setSelectedUser] = useState<User | null>(null);

    // API хуки
    const {
        data: logsData,
        isLoading: logsLoading,
        error: logsError,
        refetch: refetchLogs,
    } = useGetPaymentLogsQuery(filters);

    const { data: statsData, refetch: refetchStats } =
        useGetPaymentStatisticsQuery({
            date_from:
                filters.date_from ||
                firstDayOfMonth.toISOString().split("T")[0], // 1 число текущего месяца
            date_to: filters.date_to || today.toISOString().split("T")[0], // Сегодня (включая весь день)
        });

    const { refetch: fetchExportData } = useExportPaymentLogsQuery(filters, {
        skip: true, // Запрашиваем только по требованию
    });

    // Автоматическое обновление данных при изменении фильтров
    useEffect(() => {
        refetchLogs();
        refetchStats();
    }, [filters, refetchLogs, refetchStats]);

    // Принудительно устанавливаем правильные даты при загрузке
    useEffect(() => {
        if (!filters.date_from || filters.date_from === "") {
            setFilters((prev) => ({
                ...prev,
                date_from: formatDateLocal(firstDayOfMonth),
            }));
        }
        if (!filters.date_to || filters.date_to === "") {
            setFilters((prev) => ({
                ...prev,
                date_to: formatDateLocal(today),
            }));
        }
    }, [firstDayOfMonth, today, filters.date_from, filters.date_to]);

    const handleFilterChange = (
        key: keyof PaymentLogFilters,
        value: string
    ) => {
        setFilters((prev) => ({
            ...prev,
            [key]: value,
            page: key !== "page" ? 1 : prev.page, // Сбрасываем страницу при изменении фильтров
        }));
    };

    const handleUserSelect = (user: User) => {
        setSelectedUser(user);
        setFilters((prev) => ({
            ...prev,
            user_id: user.id.toString(),
            page: 1,
        }));
    };

    const handleUserIdChange = (value: string) => {
        setFilters((prev) => ({
            ...prev,
            user_id: value,
            page: 1,
        }));
        // Если пользователь очистил поле, сбрасываем выбранного пользователя
        if (!value) {
            setSelectedUser(null);
        }
    };

    const handlePageChange = (page: number) => {
        setFilters((prev) => ({ ...prev, page }));
    };

    const handleExport = async () => {
        try {
            const result = await fetchExportData().unwrap();
            const csvContent = convertToCSV(result.data);
            downloadCSV(csvContent, result.filename);
        } catch (error) {
            console.error("Ошибка экспорта:", error);
        }
    };

    // Функции для работы с модалкой
    const openModal = (log: any) => {
        setSelectedLog(log);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setSelectedLog(null);
        setIsModalOpen(false);
    };

    const convertToCSV = (data: any[]) => {
        if (data.length === 0) return "";

        const headers = Object.keys(data[0]).join(",");
        const rows = data.map((row) =>
            Object.values(row)
                .map((value) =>
                    typeof value === "string" && value.includes(",")
                        ? `"${value}"`
                        : value
                )
                .join(",")
        );

        return [headers, ...rows].join("\n");
    };

    const downloadCSV = (content: string, filename: string) => {
        const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = filename;
        link.click();
    };

    // Функции форматирования теперь встроены в JSX

    const getResultColor = (result: string) => {
        switch (result) {
            case "paid":
                return "text-green-600 bg-green-100";
            case "blocked":
                return "text-orange-600 bg-orange-100";
            case "warning":
                return "text-yellow-600 bg-yellow-100";
            case "deleted":
                return "text-red-600 bg-red-100";
            default:
                return "text-gray-600 bg-gray-100";
        }
    };

    const getResultText = (result: string) => {
        switch (result) {
            case "paid":
                return "Оплачено";
            case "blocked":
                return "Заблокирован";
            case "warning":
                return "Отправлено предупреждение";
            case "deleted":
                return "Удален";
            default:
                return result;
        }
    };

    return (
        <div className="p-6">
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                    Статистика платежей
                </h1>
                <p className="text-gray-600 dark:text-gray-400">
                    Детальная статистика автоматических платежей членства
                </p>
            </div>

            {/* Фильтры */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-6">
                <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
                    Фильтры
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Пользователь
                        </label>
                        <UserAutocomplete
                            value={filters.user_id}
                            onChange={handleUserIdChange}
                            onUserSelect={handleUserSelect}
                            placeholder="ID, email, phone, name..."
                            className="w-full"
                        />
                        {selectedUser && (
                            <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                                Выбран: {selectedUser.name}{" "}
                                {selectedUser.surname} (#{selectedUser.id})
                            </div>
                        )}
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Результат
                        </label>
                        <select
                            value={filters.result}
                            onChange={(e) =>
                                handleFilterChange("result", e.target.value)
                            }
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                        >
                            <option value="">Все результаты</option>
                            <option value="paid">Оплачено</option>
                            <option value="warning">
                                Отправлено предупреждение
                            </option>
                            <option value="blocked">Заблокирован</option>
                            <option value="deleted">Удален</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Дата от
                        </label>
                        <input
                            type="date"
                            value={
                                filters.date_from ||
                                formatDateLocal(firstDayOfMonth)
                            }
                            onChange={(e) =>
                                handleFilterChange("date_from", e.target.value)
                            }
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Дата до
                        </label>
                        <input
                            type="date"
                            value={filters.date_to || formatDateLocal(today)}
                            onChange={(e) =>
                                handleFilterChange("date_to", e.target.value)
                            }
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                        />
                    </div>
                </div>
                <div className="mt-4 flex gap-2">
                    <button
                        onClick={() => {
                            setFilters({
                                user_id: "",
                                result: "",
                                date_from: formatDateLocal(firstDayOfMonth),
                                date_to: formatDateLocal(today),
                                page: 1,
                                per_page: 20,
                            });
                            setSelectedUser(null);
                        }}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-md transition-colors"
                    >
                        Сбросить
                    </button>
                    <button
                        onClick={handleExport}
                        className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors"
                    >
                        Экспорт CSV
                    </button>
                </div>
            </div>

            {/* Общая статистика */}

            {statsData && (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-6">
                    <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
                        Общая статистика
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {/* Общее количество */}
                        <div className="text-center">
                            <div className="text-2xl font-bold text-blue-600">
                                {Array.isArray(statsData.data.results_stats)
                                    ? statsData.data.results_stats.reduce(
                                          (sum: number, item: any) =>
                                              sum + item.count,
                                          0
                                      )
                                    : Object.values(
                                          statsData.data.results_stats
                                      ).reduce(
                                          (sum: number, item: any) =>
                                              sum + item.count,
                                          0
                                      )}
                            </div>
                            <div className="text-sm text-gray-600 dark:text-gray-400">
                                Всего попыток
                            </div>
                        </div>

                        {/* Динамические результаты */}
                        {Array.isArray(statsData.data.results_stats)
                            ? statsData.data.results_stats.map((item: any) => (
                                  <div
                                      key={item.result}
                                      className="text-center"
                                  >
                                      <div
                                          className={`text-2xl font-bold ${
                                              item.result === "paid"
                                                  ? "text-green-600"
                                                  : item.result === "warning"
                                                  ? "text-yellow-600"
                                                  : item.result === "blocked"
                                                  ? "text-red-600"
                                                  : item.result === "deleted"
                                                  ? "text-gray-600"
                                                  : "text-blue-600"
                                          }`}
                                      >
                                          {item.count}
                                      </div>
                                      <div className="text-sm text-gray-600 dark:text-gray-400">
                                          {item.result === "paid"
                                              ? "Успешных платежей"
                                              : item.result === "warning"
                                              ? "Предупреждения"
                                              : item.result === "blocked"
                                              ? "Заблокированы"
                                              : item.result === "deleted"
                                              ? "Удалены"
                                              : item.result}
                                      </div>
                                  </div>
                              ))
                            : Object.entries(statsData.data.results_stats).map(
                                  ([key, value]: [string, any]) => (
                                      <div key={key} className="text-center">
                                          <div
                                              className={`text-2xl font-bold ${
                                                  key === "paid"
                                                      ? "text-green-600"
                                                      : key === "warning"
                                                      ? "text-yellow-600"
                                                      : key === "blocked"
                                                      ? "text-red-600"
                                                      : key === "deleted"
                                                      ? "text-gray-600"
                                                      : "text-blue-600"
                                              }`}
                                          >
                                              {value.count}
                                          </div>
                                          <div className="text-sm text-gray-600 dark:text-gray-400">
                                              {key === "paid"
                                                  ? "Успешных платежей"
                                                  : key === "warning"
                                                  ? "Предупреждения"
                                                  : key === "blocked"
                                                  ? "Заблокированы"
                                                  : key === "deleted"
                                                  ? "Удалены"
                                                  : key}
                                          </div>
                                      </div>
                                  )
                              )}
                    </div>
                </div>
            )}

            {/* Таблица логов */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
                <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Логи платежей
                    </h2>
                </div>

                {logsLoading ? (
                    <div className="p-6 text-center">
                        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        <p className="mt-2 text-gray-600 dark:text-gray-400">
                            Загрузка...
                        </p>
                    </div>
                ) : logsError ? (
                    <div className="p-6 text-center text-red-600 dark:text-red-400">
                        Ошибка загрузки данных
                    </div>
                ) : !logsData?.data.data || logsData.data.data.length === 0 ? (
                    <div className="p-6 text-center text-gray-600 dark:text-gray-400">
                        Логи не найдены
                    </div>
                ) : (
                    <>
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                <thead className="bg-gray-50 dark:bg-gray-700">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                            ID
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                            Пользователь
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                            Время обработки
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                            Длительность
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                            Результат
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                            Детали
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                    {logsData.data.data.map((log: any) => (
                                        <tr
                                            key={log.id}
                                            className="hover:bg-gray-50 dark:hover:bg-gray-700"
                                        >
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                {log.id}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                <div>
                                                    <div className="font-medium">
                                                        {log.user_info?.name}{" "}
                                                        {log.user_info?.surname}
                                                    </div>
                                                    <div className="text-gray-500 dark:text-gray-400">
                                                        ID: {log.user_id} |{" "}
                                                        {log.user_info?.email}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                {new Date(
                                                    log.processing_start_time
                                                ).toLocaleString("ru-RU")}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                {log.processing_duration
                                                    ? `${log.processing_duration.toFixed(
                                                          2
                                                      )}с`
                                                    : "N/A"}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span
                                                    className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getResultColor(
                                                        log.result
                                                    )}`}
                                                >
                                                    {getResultText(log.result)}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">
                                                <button
                                                    onClick={() =>
                                                        openModal(log)
                                                    }
                                                    className="inline-flex items-center justify-center w-8 h-8 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
                                                    title="Показать детали"
                                                >
                                                    <svg
                                                        className="w-5 h-5"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                                        />
                                                    </svg>
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Пагинация */}
                        {logsData.data.last_page > 1 && (
                            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
                                <div className="flex items-center justify-between">
                                    <div className="text-sm text-gray-700 dark:text-gray-300">
                                        Страница {logsData.data.current_page} из{" "}
                                        {logsData.data.last_page}
                                    </div>
                                    <div className="flex space-x-2">
                                        <button
                                            onClick={() =>
                                                handlePageChange(
                                                    logsData.data.current_page -
                                                        1
                                                )
                                            }
                                            disabled={
                                                logsData.data.current_page === 1
                                            }
                                            className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-700 dark:text-white dark:border-gray-600 dark:hover:bg-gray-600"
                                        >
                                            Предыдущая
                                        </button>
                                        <button
                                            onClick={() =>
                                                handlePageChange(
                                                    logsData.data.current_page +
                                                        1
                                                )
                                            }
                                            disabled={
                                                logsData.data.current_page ===
                                                logsData.data.last_page
                                            }
                                            className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-700 dark:text-white dark:border-gray-600 dark:hover:bg-gray-600"
                                        >
                                            Следующая
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Модалка с деталями */}
            {isModalOpen && selectedLog && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                    <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white dark:bg-gray-800">
                        <div className="mt-3">
                            {/* Заголовок */}
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                                    Детали платежа #{selectedLog.id}
                                </h3>
                                <button
                                    onClick={closeModal}
                                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                >
                                    <svg
                                        className="w-6 h-6"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M6 18L18 6M6 6l12 12"
                                        />
                                    </svg>
                                </button>
                            </div>

                            {/* Содержимое модалки */}
                            <div className="space-y-6">
                                {/* Информация о пользователе */}
                                <div>
                                    <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-3">
                                        Информация о пользователе
                                    </h4>
                                    <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                                    ID:
                                                </span>
                                                <span className="ml-2 text-sm text-gray-900 dark:text-white">
                                                    {selectedLog.user_info?.id}
                                                </span>
                                            </div>
                                            <div>
                                                <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                                    Имя:
                                                </span>
                                                <span className="ml-2 text-sm text-gray-900 dark:text-white">
                                                    {
                                                        selectedLog.user_info
                                                            ?.name
                                                    }{" "}
                                                    {
                                                        selectedLog.user_info
                                                            ?.surname
                                                    }
                                                </span>
                                            </div>
                                            <div className="md:col-span-2">
                                                <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                                    Email:
                                                </span>
                                                <span className="ml-2 text-sm text-gray-900 dark:text-white">
                                                    {
                                                        selectedLog.user_info
                                                            ?.email
                                                    }
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Баланс кошелька до операции */}
                                <div>
                                    <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-3">
                                        Баланс кошелька до операции
                                    </h4>
                                    <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                                    USD:
                                                </span>
                                                <span className="ml-2 text-sm text-gray-900 dark:text-white">
                                                    {selectedLog
                                                        .wallet_balance_before
                                                        ?.main_wallet ||
                                                        "0.00000000"}
                                                </span>
                                            </div>
                                            <div>
                                                <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                                    USDT:
                                                </span>
                                                <span className="ml-2 text-sm text-gray-900 dark:text-white">
                                                    {selectedLog
                                                        .wallet_balance_before
                                                        ?.usdt_wallet ||
                                                        "0.00000000"}
                                                </span>
                                            </div>
                                            <div>
                                                <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                                    USD Future:
                                                </span>
                                                <span className="ml-2 text-sm text-gray-900 dark:text-white">
                                                    {selectedLog
                                                        .wallet_balance_before
                                                        ?.main_future_wallet ||
                                                        "0.00000000"}
                                                </span>
                                            </div>
                                            <div>
                                                <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                                    USDT Future:
                                                </span>
                                                <span className="ml-2 text-sm text-gray-900 dark:text-white">
                                                    {selectedLog
                                                        .wallet_balance_before
                                                        ?.usdt_future_wallet ||
                                                        "0.00000000"}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Спонсоры */}
                                {selectedLog.sponsors &&
                                    selectedLog.sponsors.length > 0 && (
                                        <div>
                                            <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-3">
                                                Спонсоры (
                                                {selectedLog.sponsors.length})
                                            </h4>
                                            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                                                <div className="space-y-3">
                                                    {selectedLog.sponsors.map(
                                                        (
                                                            sponsor: any,
                                                            index: number
                                                        ) => (
                                                            <div
                                                                key={index}
                                                                className="flex items-center justify-between p-3 bg-white dark:bg-gray-600 rounded border"
                                                            >
                                                                <div className="flex-1">
                                                                    <div className="flex items-center space-x-2">
                                                                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                                                                            {
                                                                                sponsor.sponsor_name
                                                                            }{" "}
                                                                            {
                                                                                sponsor.sponsor_surname
                                                                            }
                                                                        </span>
                                                                        <span className="text-xs text-gray-500 dark:text-gray-400">
                                                                            (ID:{" "}
                                                                            {
                                                                                sponsor.sponsor_id
                                                                            }
                                                                            )
                                                                        </span>
                                                                    </div>
                                                                    <div className="flex items-center space-x-4 mt-1">
                                                                        <span className="text-xs text-gray-500 dark:text-gray-400">
                                                                            Уровень:{" "}
                                                                            {
                                                                                sponsor.level
                                                                            }
                                                                        </span>
                                                                        <span
                                                                            className={`text-xs px-2 py-1 rounded ${
                                                                                sponsor.is_blocked
                                                                                    ? "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
                                                                                    : "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                                                                            }`}
                                                                        >
                                                                            {sponsor.is_blocked
                                                                                ? "Заблокирован"
                                                                                : "Активен"}
                                                                        </span>
                                                                        {sponsor.is_manager && (
                                                                            <span className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                                                                                Менеджер
                                                                            </span>
                                                                        )}
                                                                        {sponsor.pack && (
                                                                            <span className="text-xs px-2 py-1 rounded bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                                                                                Пакет:{" "}
                                                                                {
                                                                                    sponsor.pack
                                                                                }
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                {/* Детали операции */}
                                {selectedLog.details && (
                                    <div>
                                        <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-3">
                                            Детали операции
                                        </h4>
                                        <div className="space-y-4">
                                            {/* Основная информация */}
                                            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                                                <h5 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                                                    Основная информация
                                                </h5>
                                                <div className="space-y-2">
                                                    {selectedLog.details
                                                        .action && (
                                                        <div className="flex justify-between">
                                                            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                                                Действие:
                                                            </span>
                                                            <span className="text-sm text-gray-900 dark:text-white">
                                                                {
                                                                    selectedLog
                                                                        .details
                                                                        .action
                                                                }
                                                            </span>
                                                        </div>
                                                    )}
                                                    {selectedLog.details
                                                        .currency && (
                                                        <div className="flex justify-between">
                                                            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                                                Валюта:
                                                            </span>
                                                            <span className="text-sm text-gray-900 dark:text-white">
                                                                {
                                                                    selectedLog
                                                                        .details
                                                                        .currency
                                                                }
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Списание средств */}
                                            {selectedLog.details
                                                .payment_deduction && (
                                                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                                                    <h5 className="text-sm font-semibold text-blue-900 dark:text-blue-200 mb-2">
                                                        💰 Списание средств
                                                    </h5>
                                                    <div className="space-y-2">
                                                        <div className="flex justify-between">
                                                            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                                                Сумма списания:
                                                            </span>
                                                            <span className="text-sm font-semibold text-gray-900 dark:text-white">
                                                                {
                                                                    selectedLog
                                                                        .details
                                                                        .payment_deduction
                                                                        .amount_deducted
                                                                }{" "}
                                                                {
                                                                    selectedLog
                                                                        .details
                                                                        .payment_deduction
                                                                        .currency
                                                                }
                                                            </span>
                                                        </div>
                                                        <div className="flex justify-between">
                                                            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                                                Изменение USD:
                                                            </span>
                                                            <span
                                                                className={`text-sm ${
                                                                    selectedLog
                                                                        .details
                                                                        .payment_deduction
                                                                        .balance_change
                                                                        .main_wallet <
                                                                    0
                                                                        ? "text-red-600"
                                                                        : "text-green-600"
                                                                }`}
                                                            >
                                                                {
                                                                    selectedLog
                                                                        .details
                                                                        .payment_deduction
                                                                        .balance_change
                                                                        .main_wallet
                                                                }
                                                            </span>
                                                        </div>
                                                        <div className="flex justify-between">
                                                            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                                                Изменение USDT:
                                                            </span>
                                                            <span
                                                                className={`text-sm ${
                                                                    selectedLog
                                                                        .details
                                                                        .payment_deduction
                                                                        .balance_change
                                                                        .usdt_wallet <
                                                                    0
                                                                        ? "text-red-600"
                                                                        : "text-green-600"
                                                                }`}
                                                            >
                                                                {
                                                                    selectedLog
                                                                        .details
                                                                        .payment_deduction
                                                                        .balance_change
                                                                        .usdt_wallet
                                                                }
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Реферальные выплаты */}
                                            {selectedLog.details
                                                .referral_payments && (
                                                <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                                                    <h5 className="text-sm font-semibold text-green-900 dark:text-green-200 mb-2">
                                                        👥 Реферальные выплаты
                                                    </h5>
                                                    <div className="space-y-2">
                                                        <div className="flex justify-between">
                                                            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                                                Цена членства:
                                                            </span>
                                                            <span className="text-sm font-semibold text-gray-900 dark:text-white">
                                                                {
                                                                    selectedLog
                                                                        .details
                                                                        .referral_payments
                                                                        .member_price
                                                                }{" "}
                                                                {
                                                                    selectedLog
                                                                        .details
                                                                        .referral_payments
                                                                        .currency
                                                                }
                                                            </span>
                                                        </div>
                                                        <div className="flex justify-between">
                                                            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                                                Выплачено
                                                                спонсорам:
                                                            </span>
                                                            <span className="text-sm font-semibold text-green-600">
                                                                {
                                                                    selectedLog
                                                                        .details
                                                                        .referral_payments
                                                                        .total_paid_to_sponsors
                                                                }
                                                            </span>
                                                        </div>
                                                        <div className="flex justify-between">
                                                            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                                                Остаток в казну:
                                                            </span>
                                                            <span className="text-sm font-semibold text-blue-600">
                                                                {
                                                                    selectedLog
                                                                        .details
                                                                        .referral_payments
                                                                        .remaining_for_treasury
                                                                }
                                                            </span>
                                                        </div>
                                                        {selectedLog.details
                                                            .referral_payments
                                                            .payments &&
                                                            selectedLog.details
                                                                .referral_payments
                                                                .payments
                                                                .length > 0 && (
                                                                <div className="mt-3">
                                                                    <h6 className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2">
                                                                        Детали
                                                                        выплат:
                                                                    </h6>
                                                                    <div className="space-y-1">
                                                                        {selectedLog.details.referral_payments.payments.map(
                                                                            (
                                                                                payment: any,
                                                                                index: number
                                                                            ) => (
                                                                                <div
                                                                                    key={
                                                                                        index
                                                                                    }
                                                                                    className="text-xs text-gray-600 dark:text-gray-300"
                                                                                >
                                                                                    Спонсор{" "}
                                                                                    {
                                                                                        payment.sponsor_id
                                                                                    }

                                                                                    :{" "}
                                                                                    {
                                                                                        payment.amount
                                                                                    }
                                                                                </div>
                                                                            )
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            )}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Недостаточность средств */}
                                            {selectedLog.details
                                                .insufficient_funds && (
                                                <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg">
                                                    <h5 className="text-sm font-semibold text-red-900 dark:text-red-200 mb-2">
                                                        ⚠️ Недостаточность
                                                        средств
                                                    </h5>
                                                    <div className="space-y-2">
                                                        <div className="flex justify-between">
                                                            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                                                Требуемая сумма:
                                                            </span>
                                                            <span className="text-sm font-semibold text-gray-900 dark:text-white">
                                                                {
                                                                    selectedLog
                                                                        .details
                                                                        .insufficient_funds
                                                                        .required_amount
                                                                }{" "}
                                                                {
                                                                    selectedLog
                                                                        .details
                                                                        .insufficient_funds
                                                                        .currency
                                                                }
                                                            </span>
                                                        </div>
                                                        <div className="flex justify-between">
                                                            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                                                Доступно:
                                                            </span>
                                                            <span className="text-sm text-gray-900 dark:text-white">
                                                                {
                                                                    selectedLog
                                                                        .details
                                                                        .insufficient_funds
                                                                        .available_balance
                                                                }
                                                            </span>
                                                        </div>
                                                        <div className="flex justify-between">
                                                            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                                                Недостача:
                                                            </span>
                                                            <span className="text-sm font-semibold text-red-600">
                                                                {
                                                                    selectedLog
                                                                        .details
                                                                        .insufficient_funds
                                                                        .shortage
                                                                }
                                                            </span>
                                                        </div>
                                                        <div className="flex justify-between">
                                                            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                                                Автоплатеж
                                                                доступен:
                                                            </span>
                                                            <span
                                                                className={`text-sm ${
                                                                    selectedLog
                                                                        .details
                                                                        .insufficient_funds
                                                                        .auto_payment_available
                                                                        ? "text-green-600"
                                                                        : "text-red-600"
                                                                }`}
                                                            >
                                                                {selectedLog
                                                                    .details
                                                                    .insufficient_funds
                                                                    .auto_payment_available
                                                                    ? "Да"
                                                                    : "Нет"}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Блокировка пользователя */}
                                            {selectedLog.details
                                                .user_blocking && (
                                                <div className="bg-orange-50 dark:bg-orange-900/20 p-4 rounded-lg">
                                                    <h5 className="text-sm font-semibold text-orange-900 dark:text-orange-200 mb-2">
                                                        🚫 Блокировка
                                                        пользователя
                                                    </h5>
                                                    <div className="space-y-2">
                                                        <div className="flex justify-between">
                                                            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                                                Причина:
                                                            </span>
                                                            <span className="text-sm text-gray-900 dark:text-white">
                                                                {
                                                                    selectedLog
                                                                        .details
                                                                        .user_blocking
                                                                        .blocking_reason
                                                                }
                                                            </span>
                                                        </div>
                                                        <div className="flex justify-between">
                                                            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                                                Время
                                                                блокировки:
                                                            </span>
                                                            <span className="text-sm text-gray-900 dark:text-white">
                                                                {new Date(
                                                                    selectedLog
                                                                        .details
                                                                        .user_blocking
                                                                        .calculated_blocking_time *
                                                                        1000
                                                                ).toLocaleString()}
                                                            </span>
                                                        </div>
                                                        <div className="flex justify-between">
                                                            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                                                Текущее время:
                                                            </span>
                                                            <span className="text-sm text-gray-900 dark:text-white">
                                                                {new Date(
                                                                    selectedLog
                                                                        .details
                                                                        .user_blocking
                                                                        .current_time *
                                                                        1000
                                                                ).toLocaleString()}
                                                            </span>
                                                        </div>
                                                        <div className="flex justify-between">
                                                            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                                                Был
                                                                заблокирован:
                                                            </span>
                                                            <span
                                                                className={`text-sm ${
                                                                    selectedLog
                                                                        .details
                                                                        .user_blocking
                                                                        .user_status_before
                                                                        .is_blocked
                                                                        ? "text-red-600"
                                                                        : "text-green-600"
                                                                }`}
                                                            >
                                                                {selectedLog
                                                                    .details
                                                                    .user_blocking
                                                                    .user_status_before
                                                                    .is_blocked
                                                                    ? "Да"
                                                                    : "Нет"}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Кнопка закрытия */}
                            <div className="mt-6 flex justify-end">
                                <button
                                    onClick={closeModal}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-md transition-colors dark:bg-gray-600 dark:text-white dark:hover:bg-gray-500"
                                >
                                    Закрыть
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PaymentStatsPage;
